import React, { useEffect, useRef, useState } from "react";
import { useUser } from "../../../contexts/UserContext";
import "simplebar-react/dist/simplebar.min.css";

import chatbotImage from "../../../assets/images/logo192.png";
import LoadingState from "../../common/LoadingState";
import { getWebSocketBaseUrl } from "../../../config/api";
import MessageList from "./chat/MessageList";

export interface ChatMessage {
  sender: "user" | "bot";
  message: string;
  timestamp: number;
  isThinking?: boolean;
  isError?: boolean;
}

interface ChatProps {
  className?: string;
}

let thinkingDotsInterval: NodeJS.Timer | null = null;

const startThinkingDots = (
  setThinkingDots: React.Dispatch<React.SetStateAction<string>>,
) => {
  const initialThinkingMessage = "Thinking";

  const dots = [".  ", ".. ", "...", "   "];
  let index = 0;

  const updateThinkingDots = () => {
    setThinkingDots(initialThinkingMessage + dots[index]);
    index = (index + 1) % dots.length;
  };

  thinkingDotsInterval = setInterval(updateThinkingDots, 500);
};

const stopThinkingDots = (
  setThinkingDots: React.Dispatch<React.SetStateAction<string>>,
) => {
  if (thinkingDotsInterval) {
    clearInterval(thinkingDotsInterval);
    setThinkingDots("");
    thinkingDotsInterval = null;
  }
};

const ChatComponent: React.FC<ChatProps> = ({ className }) => {
  const [userInput, setUserInput] = useState("");
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [userMessagesHistory, setUserMessagesHistory] = useState<string[]>([]);
  const [thinkingDots, setThinkingDots] = useState("");
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Message history state
  const [isHistoryVisible, setIsHistoryVisible] = useState(false);
  const [historyIndex, setHistoryIndex] = useState<number | null>(null);

  const { currentUser, loading: userLoading } = useUser();

  const currentBotMessageRef = useRef<string>("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  const socketRef = useRef<WebSocket | null>(null);
  const listItemRefs = useRef<(HTMLLIElement | null)[]>([]); // Refs for history items
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Close chat when pressing Escape key in expanded mode
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isExpanded && event.key === "Escape") {
        setIsExpanded(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isExpanded]);

  // Prevent background scroll when expanded
  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = "hidden";
      inputRef.current?.focus();
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isExpanded]);

  useEffect(() => {
    const storedMessages = localStorage.getItem("chatMessages");
    if (storedMessages) {
      const parsedMessages: ChatMessage[] = JSON.parse(storedMessages);
      setChatMessages(parsedMessages);

      // Extract user messages and remove duplicates, keeping the most recent ones
      const userMessages = Array.from(
        new Set(
          parsedMessages
            .filter((msg) => msg.sender === "user")
            .reverse()
            .map((msg) => msg.message),
        ),
      ).reverse();

      setUserMessagesHistory(userMessages);
    }
  }, []);

  // Update userMessagesHistory when chatMessages change
  useEffect(() => {
    const userMessages = Array.from(
      new Set(
        chatMessages
          .filter((msg) => msg.sender === "user")
          .reverse()
          .map((msg) => msg.message),
      ),
    ).reverse();

    setUserMessagesHistory(userMessages);
  }, [chatMessages]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const connectWebSocket = async (accessToken: string) => {
    if (accessToken) {
      const socket = new WebSocket(
        `${getWebSocketBaseUrl()}/api/v1/async/chatbot`,
      );

      socketRef.current = socket;

      socket.onopen = () => {
        const existingSessionId = localStorage.getItem("chatSessionId");
        const currentSessionId = existingSessionId || crypto.randomUUID();

        if (!existingSessionId) {
          localStorage.setItem("chatSessionId", currentSessionId);
        }
        setSessionId(currentSessionId);

        socket.send(
          JSON.stringify({
            type: "authorization",
            token: accessToken,
            session_id: currentSessionId,
          }),
        );
        setIsSocketConnected(true);
        setIsReconnecting(false);
      };

      socket.onmessage = (event) => {
        const message = JSON.parse(event.data);
        const data = message["content"];
        console.debug("event: ", event);
        console.debug("data: ", data);

        if (message.type === "error") {
          stopThinkingDots(setThinkingDots);
          currentBotMessageRef.current = "";

          setChatMessages((prevMessages) => {
            const updatedMessages = prevMessages.filter(
              (msg) => !msg.isThinking,
            );
            return [
              ...updatedMessages,
              {
                sender: "bot",
                message: `An error occurred. Please try again.`,
                timestamp: Date.now(),
                isThinking: false,
                isError: true,
              },
            ];
          });

          scrollToBottom();
        } else if (data === "END_OF_MESSAGE") {
          currentBotMessageRef.current = "";
          scrollToBottom();
        } else {
          stopThinkingDots(setThinkingDots);
          currentBotMessageRef.current += data;
          setChatMessages((prevMessages) => {
            const updatedMessages = [...prevMessages];
            const lastMessageIndex = updatedMessages.length - 1;
            const currentBotMessage = currentBotMessageRef.current;
            const newMessage: ChatMessage = {
              sender: "bot",
              message: currentBotMessage,
              timestamp: Date.now(),
              isThinking: false,
            };

            if (
              updatedMessages[lastMessageIndex] &&
              updatedMessages[lastMessageIndex].sender === "bot"
            ) {
              updatedMessages[lastMessageIndex] = newMessage;
            } else {
              updatedMessages.push(newMessage);
            }

            localStorage.setItem(
              "chatMessages",
              JSON.stringify(updatedMessages),
            );

            scrollToBottom();

            return updatedMessages;
          });
        }
      };

      socket.onerror = (error) => {
        console.error("WebSocket error:", error);
        stopThinkingDots(setThinkingDots);
        currentBotMessageRef.current = "";

        setChatMessages((prevMessages) => {
          const updatedMessages = prevMessages.filter((msg) => !msg.isThinking);
          return [
            ...updatedMessages,
            {
              sender: "bot",
              message:
                "An error occurred. Please check your connection and try again.",
              timestamp: Date.now(),
              isThinking: false,
              isError: true,
            },
          ];
        });

        scrollToBottom();
      };

      socket.onclose = () => {
        setIsSocketConnected(false);
        setIsReconnecting(false);
      };
    }
  };

  useEffect(() => {
    const initializeWebSocket = async () => {
      if (!currentUser) {
        return;
      }
      const accessToken = await currentUser.getIdToken();

      await connectWebSocket(accessToken);
    };

    if (!userLoading) {
      initializeWebSocket();
    }

    const currentSocket = socketRef.current;

    return () => {
      currentSocket?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, userLoading]);

  const handleUserInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserInput(e.target.value);
    setIsHistoryVisible(false);
    setHistoryIndex(null);
  };

  const handleSendMessage = () => {
    if (userInput.trim()) {
      setChatMessages((prevMessages) => {
        const updatedMessages = [
          ...prevMessages.filter((msg) => !msg.isThinking && !msg.isError),
          {
            sender: "user" as const,
            message: userInput,
            timestamp: Date.now(),
          },
          {
            sender: "bot" as const,
            message: "Thinking",
            timestamp: Date.now(),
            isThinking: true,
          },
        ];

        localStorage.setItem("chatMessages", JSON.stringify(updatedMessages));
        return updatedMessages;
      });

      socketRef.current?.send(
        JSON.stringify({
          type: "message",
          content: userInput,
          session_id: sessionId,
        }),
      );
      setUserInput("");
      startThinkingDots(setThinkingDots);
      inputRef.current?.focus();

      // Reset history navigation
      setIsHistoryVisible(false);
      setHistoryIndex(null);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isHistoryVisible) {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (historyIndex !== null && userMessagesHistory.length > 0) {
          if (historyIndex > 0) {
            setHistoryIndex(historyIndex - 1);
          } else {
            setHistoryIndex(userMessagesHistory.length - 1); // Wrap to last message
          }
        }
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        if (historyIndex !== null && userMessagesHistory.length > 0) {
          if (historyIndex < userMessagesHistory.length - 1) {
            setHistoryIndex(historyIndex + 1);
          } else {
            setHistoryIndex(0); // Wrap to first message
          }
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (historyIndex !== null && userMessagesHistory.length > 0) {
          setUserInput(userMessagesHistory[historyIndex]);
        }
        setIsHistoryVisible(false);
        setHistoryIndex(null);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setIsHistoryVisible(false);
        setHistoryIndex(null);
      } else {
        // Any other key press hides the history
        setIsHistoryVisible(false);
        setHistoryIndex(null);
      }
    } else {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (userMessagesHistory.length > 0) {
          setIsHistoryVisible(true);
          setHistoryIndex(userMessagesHistory.length - 1); // Start at the last (most recent) message
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        handleSendMessage();
      }
    }
  };

  // Update input value when historyIndex changes
  useEffect(() => {
    if (isHistoryVisible && historyIndex !== null) {
      const newInput = userMessagesHistory[historyIndex];
      setUserInput(newInput);
      // Move cursor to the end
      setTimeout(() => {
        inputRef.current?.setSelectionRange(newInput.length, newInput.length);
      }, 0);
      // Scroll selected item into view
      listItemRefs.current[historyIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [historyIndex, isHistoryVisible, userMessagesHistory]);

  // Handle clicks outside of input and dropdown to hide history
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsHistoryVisible(false);
        setHistoryIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const reconnectWebSocket = async () => {
    function sleep(ms: number): Promise<void> {
      return new Promise((resolve) => setTimeout(resolve, ms));
    }

    setIsReconnecting(true);
    await sleep(2000);

    if (currentUser) {
      const accessToken = await currentUser.getIdToken();
      await connectWebSocket(accessToken);
    }
  };

  if (userLoading) {
    return <LoadingState message={"Loading..."} />;
  }

  if (!currentUser) {
    return <div>Please log in to use the chat feature.</div>;
  }

  return (
    <>
      {isExpanded && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 transition-all duration-300" />
      )}
      <div
        className={`rounded-xl shadow-xl bg-white dark:bg-slate-900 flex flex-col ${
          className ? className : ""
        } ${
          isExpanded
            ? "fixed inset-0 z-50 m-4 scale-100 opacity-100 transition-all duration-300"
            : "h-[500px]"
        }`}
        style={
          isExpanded
            ? { height: "calc(100% - 2rem)", width: "calc(100% - 2rem)" }
            : { height: "500px" }
        }
      >
        <div className="flex justify-between items-center bg-gradient-to-r from-indigo-600 via-purple-600 to-purple-800 text-base p-4 rounded-t-xl">
          <div className="flex items-center">
            <img
              src={chatbotImage}
              className="w-11 h-11 rounded-full ring-2 ring-white/20"
              alt="AI Assistant"
            />
            <div className="ml-3">
              {/* Updated text color to white */}
              <span className="block font-semibold text-lg text-white">
                AI Assistant
              </span>
              <span className="text-sm flex items-center text-white/80">
                {isSocketConnected ? (
                  <>
                    <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse" />
                    Online
                  </>
                ) : (
                  <>
                    <span className="w-2 h-2 bg-red-400 rounded-full mr-2" />
                    Offline
                  </>
                )}
              </span>
            </div>
          </div>
          <button
            onClick={toggleExpand}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            {isExpanded ? (
              // Updated icon color to white
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 12H4"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 12h16M12 4v16"
                />
              </svg>
            )}
          </button>
        </div>

        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600"
        >
          <div className="p-4 space-y-4">
            <MessageList
              chatMessages={chatMessages}
              currentUser={currentUser}
              thinkingDots={thinkingDots}
            />
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 relative">
          <div className="flex items-center gap-3">
            <div
              className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                isSocketConnected
                  ? "bg-green-500"
                  : isReconnecting
                    ? "bg-yellow-500 animate-pulse"
                    : "bg-red-500"
              }`}
            />
            <input
              type="text"
              ref={inputRef}
              className="flex-1 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-600 focus:border-transparent transition-all"
              placeholder="Ask anything about your finances..."
              value={userInput}
              onChange={handleUserInput}
              onKeyDown={handleInputKeyDown}
              aria-label="Chat input"
            />
            {isSocketConnected ? (
              <button
                onClick={handleSendMessage}
                className="p-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                aria-label="Send message"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </button>
            ) : (
              <button
                onClick={reconnectWebSocket}
                className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                aria-label="Reconnect"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Enhanced History Dropdown */}
          {isHistoryVisible && (
            <div
              className="absolute bottom-14 left-4 right-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto z-10"
              role="listbox"
              aria-label="Message history"
            >
              <ul>
                {userMessagesHistory.map((message, index) => (
                  <li
                    key={index}
                    ref={(el) => (listItemRefs.current[index] = el)}
                    className={`px-4 py-2 cursor-pointer select-none ${
                      historyIndex === index
                        ? "bg-indigo-100 dark:bg-indigo-700"
                        : "hover:bg-gray-100 dark:hover:bg-gray-700"
                    }`}
                    onMouseDown={() => {
                      setUserInput(message);
                      setIsHistoryVisible(false);
                      setHistoryIndex(null);
                      inputRef.current?.focus();
                    }}
                    role="option"
                    aria-selected={historyIndex === index}
                  >
                    {message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ChatComponent;
